import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useActivePatient } from '@context/ActivePatientContext'
import { cn } from '@lib/utils'
import {
  LayoutDashboard,
  Calendar,
  User,
  Heart,
  Stethoscope,
  Sparkles,
  type LucideIcon, MessageSquareText } from 'lucide-react'

interface Section {
  nameKey: string
  descKey: string
  path: string
  icon: LucideIcon
}

const sections: Section[] = [
  { nameKey: 'workspace.tab_summary',            descKey: 'workspace.tab_summary_desc',            path: 'resumen',          icon: Sparkles },
  { nameKey: 'workspace.tab_patient_data',       descKey: 'workspace.tab_patient_data_desc',       path: 'datos-paciente',   icon: User },
  { nameKey: 'workspace.tab_diagnosis',          descKey: 'workspace.tab_diagnosis_desc',          path: 'diagnostico',      icon: LayoutDashboard },
  { nameKey: 'workspace.tab_progress_notes',     descKey: 'workspace.tab_progress_notes_desc',     path: 'consulta',         icon: Stethoscope },
  { nameKey: 'workspace.tab_clinical_history',   descKey: 'workspace.tab_clinical_history_desc',   path: 'historia-clinica', icon: Heart },
  { nameKey: 'workspace.tab_appointment_history',descKey: 'workspace.tab_appointment_history_desc',path: 'historial-citas',  icon: Calendar },
  { nameKey: 'workspace.tab_reception_notes',    descKey: 'workspace.tab_reception_notes_desc',    path: 'notas',            icon: MessageSquareText },
]

export function PatientWorkspaceTabs({ vertical = false }: { vertical?: boolean }) {
  const { t } = useTranslation()
  const location = useLocation()
  const { activePatientId } = useActivePatient()

  if (!activePatientId) return null

  // Determine active section from URL
  const currentPath = location.pathname.split('/').pop() || 'diagnostico'

  // Vertical variant — used in the left sidebar as a stacked navigation list.
  if (vertical) {
    return (
      <nav className="flex flex-col gap-2" aria-label="Patient workspace sections">
        <p className="px-3 text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/50">
          {t('workspace.sections_label')}
        </p>
        <div className="flex flex-col gap-2.5">
          {sections.map((section) => {
            const Icon = section.icon
            const isActive = currentPath === section.path

            return (
              <NavLink
                key={section.path}
                to={section.path}
                className={cn(
                  'group flex items-start gap-3 rounded-xl px-3.5 py-3.5 transition-colors',
                  isActive
                    ? 'bg-ml-primary/10'
                    : 'hover:bg-ml-surface-container-low'
                )}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon
                  className={cn(
                    'mt-0.5 h-5 w-5 flex-shrink-0 transition-colors',
                    isActive
                      ? 'text-ml-primary'
                      : 'text-ml-on-surface-variant/60 group-hover:text-ml-primary'
                  )}
                  aria-hidden="true"
                />
                <span className="min-w-0">
                  <span
                    className={cn(
                      'block text-[15px] leading-tight transition-colors',
                      isActive
                        ? 'font-bold text-ml-primary'
                        : 'font-medium text-ml-on-surface group-hover:text-ml-primary'
                    )}
                  >
                    {t(section.nameKey)}
                  </span>
                  <span className="mt-1 block text-xs leading-snug text-ml-on-surface-variant/60">
                    {t(section.descKey)}
                  </span>
                </span>
              </NavLink>
            )
          })}
        </div>
      </nav>
    )
  }

  return (
    <div className="bg-ml-surface sticky top-0 z-10 border-b border-ml-outline-variant/15">
      {/* Desktop nav */}
      <nav
        className="hidden md:flex px-6 lg:px-12 gap-8 overflow-x-auto scrollbar-hide"
        aria-label="Patient workspace sections"
      >
        {sections.map((section) => {
          const Icon = section.icon
          const isActive = currentPath === section.path

          return (
            <NavLink
              key={section.path}
              to={section.path}
              className={cn(
                "flex items-center gap-2 pb-3.5 pt-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                isActive
                  ? "border-ml-primary text-ml-primary font-bold"
                  : "border-transparent text-ml-on-surface-variant/60 hover:text-ml-primary hover:border-ml-outline-variant/30"
              )}
              aria-current={isActive ? 'page' : undefined}
              title={t(section.descKey)}
            >
              <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              {t(section.nameKey)}
            </NavLink>
          )
        })}
      </nav>

      {/* Mobile pills */}
      <div className="md:hidden px-4 py-3">
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {sections.map((section) => {
            const Icon = section.icon
            const isActive = currentPath === section.path

            return (
              <NavLink
                key={section.path}
                to={section.path}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded whitespace-nowrap transition-colors uppercase tracking-wider flex-shrink-0",
                  isActive
                    ? "bg-ml-primary text-white"
                    : "bg-ml-surface-container-high text-ml-on-surface-variant hover:bg-ml-surface-container-highest"
                )}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                {t(section.nameKey)}
              </NavLink>
            )
          })}
        </div>
      </div>
    </div>
  )
}
